/**
 * useSavePositionTrainerResult
 *
 * POST /fun_challenges/v1/position_trainer_results/
 *
 * Idempotent on `session_id` server-side (Django uses update_or_create).
 * Both clients fire this on `fun_session_end`; the second call no-ops.
 *
 * Auth: bearer auto-injected by `ChessRestClient`. Server rejects writes
 * where `request.user.user_id` is neither `player1_id` nor `player2_id`.
 */

import {useMutation} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import type {PuzzleAttempt} from '@/Screens/FunChallenges/util/attempts';

export interface SavePositionTrainerResultPayload {
  session_id: string;
  game_type: string;                  // 'position_trainer'
  player1_id: number;
  player2_id: number;
  result: 'p1' | 'p2' | 'draw';
  reason: 'time_up' | 'forfeit' | 'mistake_out';
  puzzle_ids: number[];
  p1_attempts: PuzzleAttempt[];
  p2_attempts: PuzzleAttempt[];
  p1_solved_ids: number[];
  p1_failed_ids: number[];
  p2_solved_ids: number[];
  p2_failed_ids: number[];
  p1_score: number;
  p1_wrong: number;
  p1_accuracy: number;
  p2_score: number;
  p2_wrong: number;
  p2_accuracy: number;
  time_control_sec: number;
  max_wrong: number;
  end_reason?: 'time_up' | 'forfeit' | 'mistake_out';
}

export interface SavePositionTrainerResultResponse {
  ok: boolean;
  history_id?: number;
  error?: string;
}

export default function useSavePositionTrainerResult(
  onSuccess?: (data: SavePositionTrainerResultResponse) => void,
  onError?: (err: unknown) => void,
) {
  return useMutation<
    SavePositionTrainerResultResponse,
    unknown,
    SavePositionTrainerResultPayload
  >(
    async (payload: SavePositionTrainerResultPayload) => {
      const response = await ChessRestClient.post(
        'fun_challenges/v1/position_trainer_results/',
        payload,
      );
      return response.data as SavePositionTrainerResultResponse;
    },
    {
      onSuccess: data => {
        if (onSuccess) onSuccess(data);
      },
      onError: err => {
        if (onError) onError(err);
      },
    },
  );
}
